import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Color } from '@diritto/common/style/color';
import { ArrowIcon } from 'icons/ArrowIcon';

interface PromotionReplyListPaginatorProps {
  className?: string;
  currentPage: number;
  totalPage: number;
  onPageChange: (page: number) => void;

  maxPages?: number;
  disabled?: boolean;
}

export const PromotionReplyListPaginator = ({
  className,
  currentPage,
  totalPage,
  onPageChange,

  maxPages = 5,
  disabled = false,
  ...props
}: PromotionReplyListPaginatorProps) => {
  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPage || disabled || page === currentPage) {
        return;
      }

      onPageChange(page);
    },
    [onPageChange, totalPage, disabled, currentPage],
  );

  const currentPages = useMemo(() => {
    const pages = Array.from({ length: totalPage }, (_, i) => i + 1);

    if (pages.length <= maxPages) {
      return pages;
    }

    const start = Math.max(1, currentPage - Math.floor(maxPages / 2));
    const end = Math.min(totalPage, start + maxPages - 1);

    return pages.slice(start - 1, end);
  }, [totalPage, currentPage, maxPages]);

  const renderPage = useCallback(
    (page: number) => (
      <div
        key={page}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-[64px] ${page === currentPage ? 'bg-primary-500' : ''} cursor-pointer`}
        onClick={() => handlePageChange(page)}>
        <div
          className={`justify-start text-xxs font-regular leading-tight ${page === currentPage ? 'text-white' : 'text-black'}`}>
          {page}
        </div>
      </div>
    ),
    [currentPage, handlePageChange],
  );

  return (
    <>
      <StyledPagination className="self-stretch inline-flex justify-between items-center" {...props}>
        <div
          className="flex justify-center items-center w-8 h-8"
          onClick={() => handlePageChange(currentPage - 1)}
          style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
          <ArrowIcon direction="left" color={Color.Gray600} opacity={currentPage === 1 ? 0.3 : 1} />
        </div>
        <div className="flex justify-start items-start gap-2">{currentPages.map(page => renderPage(page))}</div>
        <div
          className="flex justify-center items-center w-8 h-8"
          onClick={() => handlePageChange(currentPage + 1)}
          style={{ cursor: currentPage === totalPage ? 'not-allowed' : 'pointer' }}>
          <ArrowIcon direction="right" color={Color.Gray600} opacity={currentPage === totalPage ? 0.3 : 1} />
        </div>
      </StyledPagination>
    </>
  );
};

const StyledPagination = styled.div`
  margin-top: 40px;
`;
